import React, { useState } from 'react';
import {
  IAuditoriaBaixaPatrimonialResultado,
  IItemBaixaPatrimonial,
  TipoMovimentoBaixaPatrimonial,
} from '../interfaces/IAuditoriaBaixaPatrimonial';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaBaixaPatrimonialResultado;
}

type FiltroStatus = 'TODOS' | 'ERRO' | 'ALERTA' | 'AVISO' | 'CONCILIADO';



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':   return 'bi-exclamation-circle-fill';
    case 'ALERTA': return 'bi-exclamation-triangle-fill';
    case 'AVISO':  return 'bi-info-circle-fill';
    default:       return 'bi-check-circle-fill';
  }
};

// COR-55 — label legível para exibição em badge de tipo de movimento
const labelTipoMovimento = (tipo: TipoMovimentoBaixaPatrimonial): string => {
  switch (tipo) {
    case 'BAIXA_COM_RESULTADO':       return 'Baixa com Resultado';
    case 'RECLASSIFICACAO_ATIVO':     return 'Reclassificação';
    case 'BAIXA_TECNICA_DEPRECIACAO': return 'Baixa Técnica';
    case 'POSSIVEL_ALIENACAO':        return 'Possível Alienação';
    case 'TRANSFERENCIA_PATRIMONIAL': return 'Transferência Patrimonial';
    case 'SEM_CONTRAPARTIDA':         return 'Sem Contrapartida';
    case 'BAIXA_SEM_REDUTORA':        return 'Sem Redutora';      // COR-58
    case 'PENDENTE_VALIDACAO':        return 'Pendente Validação'; // COR-59
    case 'RECLASSIFICACAO_AJUSTE_PENDENTE': return 'Reclassificação/Ajuste — Pendente'; // COR-255
    default:                          return '-';
  }
};

// Linha expansível reutilizável para todas as tabelas (COR-55)
interface LinhaItemProps {
  item: IItemBaixaPatrimonial;
  chave: string;
  colunas: number;
  expandido: boolean;
  onAlternar: () => void;
  corExpansao?: string;
  mostrarMulta?: boolean;
  mostrarContrapartida?: boolean;
}

const LinhaItem: React.FC<LinhaItemProps> = ({
  item, chave, colunas, expandido, onAlternar,
  corExpansao = '#fafbfc', mostrarMulta = false, mostrarContrapartida = true,
}) => (
  <React.Fragment key={chave}>
    <tr className='auditoria-row-tributo' onClick={onAlternar}>
      <td>
        <strong>{item.descricaoConta || item.contaContabil}</strong>
        {item.contaReferencial && (
          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>
        )}
      </td>
      <td>{item.categoria}</td>
      <td>{formatDate(item.dataLancamento)}</td>
      <td className='text-end'>{formatCurrency(item.valorBaixa)}</td>
      {mostrarMulta && <td className='text-end'>{formatCurrency(item.multa)}</td>}
      {mostrarContrapartida && !mostrarMulta && (
        <td>{item.contaContrapartida ? `${item.contaContrapartida} — ${item.descricaoContrapartida}` : '-'}</td>
      )}
      <td>
        {item.tipoMovimento && (
          <span className='badge bg-secondary me-1' style={{ fontSize: 10 }}>
            {labelTipoMovimento(item.tipoMovimento)}
          </span>
        )}
        <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
        <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
      </td>
    </tr>

    {expandido && (
      <tr>
        <td colSpan={colunas} style={{ background: corExpansao }}>
          <div className='py-2 px-3'>
            <div><strong>Histórico:</strong> {item.historico || '-'}</div>
            {mostrarContrapartida && item.contaContrapartida && (
              <div><strong>Contrapartida:</strong> {item.contaContrapartida} — {item.descricaoContrapartida}</div>
            )}
            <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
            {item.memoriaCalculo && (
              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>
                <strong>Memória de Cálculo:</strong> {item.memoriaCalculo}
              </div>
            )}
          </div>
        </td>
      </tr>
    )}
  </React.Fragment>
);

// COR-60 — tabela reutilizável usada dentro de cada seção e de cada grupo de conciliadas
interface TabelaItemsProps {
  items: IItemBaixaPatrimonial[];
  prefixoChave: string;
  itemExpandido: string | null;
  onAlternar: (chave: string) => void;
  mostrarMulta?: boolean;
  mostrarContrapartida?: boolean;
  corExpansao?: string;
}

const TabelaItems: React.FC<TabelaItemsProps> = ({
  items, prefixoChave, itemExpandido, onAlternar,
  mostrarMulta = false, mostrarContrapartida = true, corExpansao,
}) => (
  <div className='table-responsive mt-2'>
    <table className='auditoria-table'>
      <thead>
        <tr>
          <th>Conta</th>
          <th>Categoria</th>
          <th>Data</th>
          <th className='text-end'>Valor da Baixa</th>
          {mostrarMulta && <th className='text-end'>Multa Estimada</th>}
          {mostrarContrapartida && !mostrarMulta && <th>Contrapartida</th>}
          <th>Tipo / Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => {
          const chave = `${prefixoChave}-${item.empresaId}-${item.contaContabil}-${idx}`;
          return (
            <LinhaItem
              key={chave}
              item={item}
              chave={chave}
              colunas={mostrarMulta || !mostrarContrapartida ? 6 : 6}
              expandido={itemExpandido === chave}
              onAlternar={() => onAlternar(chave)}
              mostrarMulta={mostrarMulta}
              mostrarContrapartida={mostrarContrapartida}
              corExpansao={corExpansao}
            />
          );
        })}
      </tbody>
    </table>
  </div>
);

const AuditoriaBaixaPatrimonialDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido]             = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);
  // COR-60 — filtro de status e controles de expansão da seção de conciliadas
  const [filtroStatus, setFiltroStatus]               = useState<FiltroStatus>('TODOS');
  const [conciliadasColapsadas, setConciliadasColapsadas] = useState(true);
  const [categoriaExpandida, setCategoriaExpandida]   = useState<string | null>(null);

  const baixasSemContrapartida    = resultado.baixasSemContrapartida    || [];
  const baixasSemIdentificacao    = resultado.baixasSemIdentificacao    || [];
  const baixasPossiveisAlienacoes = resultado.baixasPossiveisAlienacoes || [];
  const baixasConciliadas         = resultado.baixasConciliadas         || [];
  // COR-255
  const baixasReclassificacaoPendente = resultado.baixasReclassificacaoPendenteJustificativa || [];

  const alternarExpandir = (chave: string) =>
    setItemExpandido(itemExpandido === chave ? null : chave);

  // COR-60 — arrays filtrados por status selecionado
  const filtrar = (lista: IItemBaixaPatrimonial[]) =>
    filtroStatus === 'TODOS' ? lista : lista.filter(x => x.status === filtroStatus);

  const scFiltradas = filtrar(baixasSemContrapartida);
  const siFiltradas = filtrar(baixasSemIdentificacao);
  const paFiltradas = filtrar(baixasPossiveisAlienacoes);
  const ccFiltradas = filtrar(baixasConciliadas);
  const rpFiltradas = filtrar(baixasReclassificacaoPendente); // COR-255

  // COR-60 — contadores por status para os botões do filtro
  const contadores: Record<FiltroStatus, number> = {
    TODOS: baixasSemContrapartida.length + baixasSemIdentificacao.length +
      baixasPossiveisAlienacoes.length + baixasConciliadas.length + baixasReclassificacaoPendente.length,
    ERRO: [...baixasSemContrapartida, ...baixasSemIdentificacao].filter(x => x.status === 'ERRO').length,
    ALERTA: [...baixasSemContrapartida, ...baixasSemIdentificacao].filter(x => x.status === 'ALERTA').length,
    // COR-255: reclassificação/ajuste pendente também conta como AVISO.
    AVISO: baixasPossiveisAlienacoes.filter(x => x.status === 'AVISO').length
      + baixasReclassificacaoPendente.filter(x => x.status === 'AVISO').length,
    CONCILIADO: baixasConciliadas.length,
  };

  // COR-60 — agrupamento das conciliadas por categoria (maior valor primeiro)
  type GrupoCategoria = { items: IItemBaixaPatrimonial[]; totalValor: number };
  const gruposConciliadas = Object.entries(
    ccFiltradas.reduce<Record<string, GrupoCategoria>>((acc, item) => {
      const cat = item.categoria || 'Sem Categoria';
      if (!acc[cat]) acc[cat] = { items: [], totalValor: 0 };
      acc[cat].items.push(item);
      acc[cat].totalValor += item.valorBaixa;
      return acc;
    }, {})
  ).sort(([, a], [, b]) => b.totalValor - a.totalValor);

  const totalConciliadasValor = ccFiltradas.reduce((s, x) => s + x.valorBaixa, 0);

  // Visibilidade das seções com base no filtro ativo
  const mostrarSc = filtroStatus === 'TODOS' || scFiltradas.length > 0;
  const mostrarSi = filtroStatus === 'TODOS' || siFiltradas.length > 0;
  const mostrarPa = filtroStatus === 'TODOS' ? baixasPossiveisAlienacoes.length > 0 : paFiltradas.length > 0;
  const mostrarCc = filtroStatus === 'TODOS' ? baixasConciliadas.length > 0 : ccFiltradas.length > 0;
  // COR-255
  const mostrarRp = filtroStatus === 'TODOS' ? baixasReclassificacaoPendente.length > 0 : rpFiltradas.length > 0;

  const filtrosBotoes: { status: FiltroStatus; label: string; classe: string }[] = [
    { status: 'TODOS',      label: 'Todos',      classe: 'btn-outline-dark'    },
    { status: 'ERRO',       label: 'Erro',        classe: 'btn-outline-danger'  },
    { status: 'ALERTA',     label: 'Alerta',      classe: 'btn-outline-warning' },
    { status: 'AVISO',      label: 'Aviso',       classe: 'btn-outline-info'    },
    { status: 'CONCILIADO', label: 'Conciliado',  classe: 'btn-outline-success' },
  ];

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Baixa Direta de Ativo Imobilizado — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multas de 3% por inexatidão no SPED (Art. 12 da Lei nº 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>
            Alertas: sem contrapartida (pendente de validação) ou sem identificação do bem. Avisos: possíveis alienações.
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada às análises desta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Baixa sem Contrapartida:</strong> {resultado.fundamentacaoContrapartida}</div>
          <div className='mt-2'><strong>Baixa sem Identificação do Bem:</strong> {resultado.fundamentacaoRastreabilidade}</div>
        </div>
      )}

      {/* COR-60 — barra de filtro por status */}
      <div className='auditoria-card' style={{ paddingTop: 12, paddingBottom: 12 }}>
        <div className='d-flex align-items-center gap-2 flex-wrap'>
          <span className='text-muted me-1' style={{ fontSize: 13 }}>Filtrar por status:</span>
          {filtrosBotoes.map(({ status, label, classe }) => (
            <button
              key={status}
              type='button'
              className={`btn btn-sm ${filtroStatus === status ? classe.replace('outline-', '') : classe}`}
              onClick={() => setFiltroStatus(status)}
            >
              {label}
              <span
                className='badge ms-1'
                style={{
                  background: filtroStatus === status ? 'rgba(255,255,255,0.35)' : '#e9ecef',
                  color: filtroStatus === status ? '#fff' : '#495057',
                }}
              >
                {contadores[status]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Tarefa 2.1 — Baixas Pendentes de Validação / Sem Contrapartida */}
      {mostrarSc && (
        <div className='auditoria-card'>
          <h3>Baixas Pendentes de Validação Documental</h3>
          <p className='text-muted'>
            Crédito (baixa) em conta de Ativo Imobilizado sem contrapartida identificável automaticamente
            (mesma data e valor, ou análise de lançamento composto). Sem impacto fiscal automático — requer validação
            documental antes de concluir como erro fiscal (CPC 27, itens 67-72).
          </p>

          {scFiltradas.length === 0 ? (
            <div className='text-muted mt-4'>Nenhuma baixa pendente de validação identificada no período.</div>
          ) : (
            <TabelaItems
              items={scFiltradas}
              prefixoChave='sc'
              itemExpandido={itemExpandido}
              onAlternar={alternarExpandir}
              mostrarMulta
              mostrarContrapartida={false}
            />
          )}
        </div>
      )}

      {/* Tarefa 2.2 — Baixa sem Identificação do Bem */}
      {mostrarSi && (
        <div className='auditoria-card'>
          <h3>Baixa sem Identificação do Bem no Histórico</h3>
          <p className='text-muted'>
            Baixa corretamente reconhecida no resultado, porém sem identificação do bem (placa, chassi, nº de série ou de
            patrimônio) no histórico — achado preventivo, sem tributação compulsória (CPC 27, item 74).
          </p>

          {siFiltradas.length === 0 ? (
            <div className='text-muted mt-4'>Nenhuma baixa sem identificação do bem identificada no período.</div>
          ) : (
            <TabelaItems
              items={siFiltradas}
              prefixoChave='si'
              itemExpandido={itemExpandido}
              onAlternar={alternarExpandir}
            />
          )}
        </div>
      )}

      {/* COR-55 — Possíveis Alienações de Ativo (AVISO) */}
      {mostrarPa && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-info-circle-fill me-2' style={{ color: '#f59e0b', fontSize: 18 }} />
            Possíveis Alienações de Ativo via Banco/Caixa
          </h3>
          <p className='text-muted'>
            Baixas de Ativo Imobilizado com contrapartida em conta bancária, de caixa ou de clientes — sugerem
            recebimento de alienação, mas nenhuma conta de resultado (ganho/perda) foi localizada no mesmo data+valor.
            Sem impacto fiscal automático: validar documentação e confirmar se o resultado foi reconhecido.
          </p>

          <TabelaItems
            items={paFiltradas}
            prefixoChave='pa'
            itemExpandido={itemExpandido}
            onAlternar={alternarExpandir}
            corExpansao='#fffbeb'
          />
        </div>
      )}

      {/* COR-255 — Reclassificação/Ajuste Pendente de Justificativa (AVISO) */}
      {mostrarRp && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-info-circle-fill me-2' style={{ color: '#f59e0b', fontSize: 18 }} />
            Reclassificação/Ajuste — Pendente de Justificativa
          </h3>
          <p className='text-muted'>
            Crédito (baixa) em conta de Ativo Imobilizado cujo histórico contém termo de reclassificação, ajuste,
            correção ou estorno — mesmo com contrapartida estruturalmente coerente com reclassificação entre contas
            do Imobilizado ou transferência patrimonial, o lançamento foi localizado, mas sua natureza indica possível
            correção de classificação contábil anterior, não necessariamente baixa patrimonial efetiva. Sem impacto
            fiscal automático: validar documentação de suporte para confirmar se houve baixa real do ativo.
          </p>

          <TabelaItems
            items={rpFiltradas}
            prefixoChave='rp'
            itemExpandido={itemExpandido}
            onAlternar={alternarExpandir}
            corExpansao='#fffbeb'
          />
        </div>
      )}

      {/* COR-60 — Baixas Conciliadas: colapsadas por padrão, agrupadas por categoria */}
      {mostrarCc && (
        <div className='auditoria-card'>
          <div
            className='d-flex justify-content-between align-items-center'
            style={{ cursor: 'pointer' }}
            onClick={() => setConciliadasColapsadas(!conciliadasColapsadas)}
          >
            <h3 className='mb-0'>
              <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
              Baixas Verificadas e Conciliadas
            </h3>
            <div className='d-flex align-items-center gap-2'>
              <span className='badge bg-success'>{ccFiltradas.length} lançamento{ccFiltradas.length !== 1 ? 's' : ''}</span>
              <span className='text-muted' style={{ fontSize: 13 }}>{formatCurrency(totalConciliadasValor)}</span>
              <i className={`bi ${conciliadasColapsadas ? 'bi-chevron-down' : 'bi-chevron-up'} text-muted`} />
            </div>
          </div>

          {!conciliadasColapsadas && (
            <>
              <p className='text-muted mt-2 mb-0' style={{ fontSize: 13 }}>
                Baixas auditadas e em conformidade: alienação com resultado reconhecido, reclassificação entre contas de
                Ativo Imobilizado, baixa técnica contra depreciação acumulada ou transferência patrimonial documentada.
                Expandido por categoria — maior valor primeiro.
              </p>

              {gruposConciliadas.length === 0 ? (
                <div className='text-muted mt-3'>Nenhuma baixa conciliada para o filtro selecionado.</div>
              ) : (
                gruposConciliadas.map(([cat, { items, totalValor }]) => (
                  <div key={cat} className='mt-3'>
                    <div
                      className='d-flex justify-content-between align-items-center px-3 py-2 rounded'
                      style={{
                        cursor: 'pointer',
                        background: categoriaExpandida === cat ? '#f0fdf4' : '#f8f9fa',
                        border: '1px solid #dee2e6',
                      }}
                      onClick={() => setCategoriaExpandida(categoriaExpandida === cat ? null : cat)}
                    >
                      <span>
                        <i className='bi bi-tag-fill text-success me-2' style={{ fontSize: 12 }} />
                        <strong>{cat}</strong>
                        <span className='text-muted ms-2' style={{ fontSize: 12 }}>
                          {items.length} lançamento{items.length !== 1 ? 's' : ''}
                        </span>
                      </span>
                      <div className='d-flex align-items-center gap-2'>
                        <span className='text-success' style={{ fontSize: 13 }}>{formatCurrency(totalValor)}</span>
                        <i className={`bi ${categoriaExpandida === cat ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} />
                      </div>
                    </div>

                    {categoriaExpandida === cat && (
                      <TabelaItems
                        items={items}
                        prefixoChave={`cc-${cat}`}
                        itemExpandido={itemExpandido}
                        onAlternar={alternarExpandir}
                        corExpansao='#f0fdf4'
                      />
                    )}
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default AuditoriaBaixaPatrimonialDetalhe;
